// 📄 pages/Soporte/AyudaHome.tsx
// "Ayuda" (navbar, visible a CUALQUIER usuario logueado): crear un ticket
// de soporte de plataforma sin necesidad de estar en un proyecto, y ver el
// estado de los propios tickets. Sin asignación — el usuario no elige (ni
// sabe) quién lo va a atender, eso lo gestiona el Admin desde SoporteHome.
import React, { useEffect, useState } from 'react';
import { LifeBuoy, Plus, X, RefreshCw } from 'lucide-react';
import { useAuth } from '../../context/AuthContext.tsx';
import { listProjectsForUser, Project } from '../../services/projectService.ts';
import {
  Ticket, TicketFormData, TicketCategoria, TicketPrioridad,
  TICKET_PRIORIDADES, TICKET_CATEGORIAS, createTicket, escucharMisTickets
} from '../../services/ticketService.ts';
import TicketConversacion from './TicketConversacion.tsx';

const emptyForm = (): TicketFormData => ({
  descripcion: '', prioridad: 'Media', categoria: 'Plataforma'
});

const ESTADO_BADGE: Record<string, string> = {
  'Abierto': 'bg-blue-500/10 text-blue-300 border-blue-500/20',
  'En revisión': 'bg-amber-500/10 text-amber-300 border-amber-500/20',
  'Resuelto': 'bg-emerald-500/10 text-emerald-300 border-emerald-500/20',
  'Cerrado': 'bg-white/5 text-gray-400 border-white/10'
};

export default function AyudaHome() {
  const { user, profile } = useAuth();
  const [proyectos, setProyectos] = useState<Project[]>([]);
  const [tickets, setTickets] = useState<Ticket[] | null>(null);
  const [modalAbierto, setModalAbierto] = useState(false);

  useEffect(() => {
    if (!user) return;
    listProjectsForUser(user.uid).then(setProyectos).catch((err) => console.error('Error cargando proyectos:', err));
    // En vivo: el estado que cambia soporte y sus respuestas se ven sin recargar.
    return escucharMisTickets(user.uid, setTickets, (err) => console.error('Error cargando tickets:', err));
  }, [user]);

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-white flex items-center gap-2">
          <LifeBuoy className="w-5 h-5 text-blue-400" /> Ayuda
        </h1>
        <button
          onClick={() => setModalAbierto(true)}
          className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
        >
          <Plus className="w-4 h-4" /> Crear ticket
        </button>
      </div>

      <p className="text-sm text-gray-400">
        ¿Tenés un problema con la plataforma, un proyecto o tu organización? Abrí un ticket y un miembro del equipo de soporte lo va a revisar.
      </p>

      <p className="text-sm text-gray-400">
        Si el problema es del día a día de tu proyecto (no ves una orden de compra, una factura fue rechazada), ábrelo en la <span className="text-gray-200">Mesa de ayuda</span> dentro del proyecto: ahí te atiende su equipo de soporte.
      </p>

      <div>
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Mis tickets</p>
        {tickets === null ? (
          <div className="p-8 text-center text-gray-400 flex flex-col items-center gap-2">
            <RefreshCw className="w-5 h-5 animate-spin text-blue-500" />
          </div>
        ) : tickets.length === 0 ? (
          <p className="text-sm text-gray-500">Todavía no abriste ningún ticket.</p>
        ) : (
          <div className="space-y-2">
            {tickets.map((t) => (
              <div key={t.id} className="bg-white/5 border border-white/10 rounded-xl p-4 space-y-2">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full border ${ESTADO_BADGE[t.estado]}`}>{t.estado}</span>
                  <span className="text-xs text-gray-500">{t.prioridad} · {t.categoria}{t.proyectoNombre ? ` · ${t.proyectoNombre}` : ''}</span>
                </div>
                <p className="text-sm text-gray-300 whitespace-pre-wrap">{t.descripcion}</p>
                <TicketConversacion ticket={t} miRol="usuario" />
              </div>
            ))}
          </div>
        )}
      </div>

      {modalAbierto && (
        <CrearTicketModal
          uid={user.uid}
          nombre={profile?.name || user.email || ''}
          email={user.email || ''}
          proyectos={proyectos}
          onClose={() => setModalAbierto(false)}
          onCreado={() => setModalAbierto(false)}
        />
      )}
    </div>
  );
}

function CrearTicketModal({
  uid, nombre, email, proyectos, onClose, onCreado
}: {
  uid: string; nombre: string; email: string; proyectos: Project[];
  onClose: () => void; onCreado: () => void;
}) {
  const [form, setForm] = useState<TicketFormData>(emptyForm());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setGuardando(true);
    try {
      const proyecto = form.categoria === 'Proyecto' ? proyectos.find(p => p.id === form.proyectoId) : undefined;
      await createTicket(uid, nombre, email, {
        ...form,
        proyectoNombre: proyecto?.nombre
      });
      onCreado();
    } catch (err: any) {
      setError(err.message || 'Error al crear el ticket.');
    } finally {
      setGuardando(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-blue-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between p-5 border-b border-white/10 flex-shrink-0">
          <h3 className="text-lg font-medium text-white">Nuevo ticket de soporte</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4 overflow-y-auto flex-1">
          {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Categoría*</label>
              <select
                value={form.categoria}
                onChange={(e) => setForm({ ...form, categoria: e.target.value as TicketCategoria, proyectoId: undefined })}
                className={inputClass}
              >
                {TICKET_CATEGORIAS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className={labelClass}>Prioridad*</label>
              <select
                value={form.prioridad}
                onChange={(e) => setForm({ ...form, prioridad: e.target.value as TicketPrioridad })}
                className={inputClass}
              >
                {TICKET_PRIORIDADES.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {form.categoria === 'Proyecto' && (
            <div>
              <label className={labelClass}>Proyecto relacionado</label>
              <select
                value={form.proyectoId || ''}
                onChange={(e) => setForm({ ...form, proyectoId: e.target.value || undefined })}
                className={inputClass}
              >
                <option value="">Seleccionar...</option>
                {proyectos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
              </select>
            </div>
          )}

          <div>
            <label className={labelClass}>Descripción*</label>
            <textarea
              value={form.descripcion}
              onChange={(e) => setForm({ ...form, descripcion: e.target.value })}
              rows={5}
              placeholder="Contanos qué pasó, con el mayor detalle posible..."
              className={inputClass}
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white">Cancelar</button>
            <button
              type="submit"
              disabled={guardando || !form.descripcion.trim()}
              className="flex items-center gap-1.5 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all"
            >
              {guardando ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              Crear ticket
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
